import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { Button, Input } from "reactstrap";
import { deleteCategory, getAllCategories, updateCategorySequence } from "../../../../api/categoryAPIs";
import {
    BlockBetween,
    BlockContent,
    BlockDes,
    BlockHead,
    BlockHeadContent,
    BlockTitle,
    PreviewCard,
    ReactDataTable,
} from "../../../../components/Component";
import DeleteModal from "../../../../components/DeleteModal";
import Content from "../../../../layout/content/Content";
import Head from "../../../../layout/head/Head";

// Custom debounce function
const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func(...args), delay);
    };
};

const CategoryList = () => {
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                setLoading(true);
                const response = await getAllCategories();
                setCategories(response.data.data);
            } catch (error) {
                console.error("Failed to fetch categories:", error);
                toast.error("Failed to fetch categories");
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
    }, []);

    const handleDelete = async (item) => {
        try {
            await deleteCategory({ id: item._id });
            setCategories(categories.filter((category) => category._id !== item._id));
            console.log("Category deleted successfully");
            toast.success("Category deleted successfully");
        } catch (error) {
            console.error("Error deleting category:", error);
            toast.error(error.response?.message || "Error deleting category");
        }
    };

    const handleCategorySequenceUpdate = async (item) => {
        try {
            await updateCategorySequence({
                formData: {
                    sequence: item.sequence,
                }, categoryId: item._id
            });
            setCategories((prev) =>
                prev.map((category) => (category._id === item._id ? { ...category, sequence: item.sequence } : category))
            );
            console.log("Category sequence updated successfully");
            toast.success("Category sequence updated successfully");
        } catch (error) {
            console.error("Error updating category sequence:", error);
            toast.error(error.response?.message || "Error updating category sequence");
        }
    };

    // Debounced version of handleCategorySequenceUpdate using custom debounce
    const debouncedHandleCategorySequenceUpdate = useCallback(
        debounce((categoryId, sequence) => {
            handleCategorySequenceUpdate({ _id: categoryId, sequence: Number(sequence) });
        }, 500),
        []
    );

    const columns = [
        {
            name: "Name",
            selector: (row) => row.name,
            sortable: true,
            cell: (row) => (
                <div
                    style={{ cursor: "pointer" }}
                    className="d-flex align-items-center m-1"
                    onClick={() => navigate(`/sub-categories/${row._id}`)}
                >
                    <img
                        src={row.images?.[0] || "https://via.placeholder.com/50"}
                        alt={row.name}
                        style={{ width: "50px", height: "50px", borderRadius: "50%", objectFit: "cover" }}
                        className="me-2"
                    />
                    <span className="fw-bold text-dark">{row.name}</span>
                </div>
            ),
        },
        {
            name: "Description",
            selector: (row) => row.description,
            sortable: true,
            cell: (row) => (
                <div className="text-muted text-truncate" style={{ maxWidth: "200px" }} title={row.description}>
                    {row.description}
                </div>
            ),
        },
        {
            name: "Status",
            selector: (row) => (row.status ? "Active" : "Inactive"),
            sortable: true,
            cell: (row) => (
                <span className={`badge rounded-pill ${row.status ? "bg-success" : "bg-danger"}`}>
                    {row.status ? "Active" : "Inactive"}
                </span>
            ),
        },
        {
            name: "Sequence",
            selector: (row) => row.sequence || 0,
            sortable: true,
            cell: (row) => (
                <div className="d-flex align-items-center gap-2">
                    <Input
                        type="number"
                        min="0"
                        value={row.sequence || 0}
                        onChange={(e) => {
                            const newSequence = e.target.value;
                            debouncedHandleCategorySequenceUpdate(row._id, newSequence);
                        }}
                        style={{ width: "80px" }}
                        className="form-control-sm"
                    />
                </div>
            ),
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="d-flex align-items-center gap-2">
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => navigate(`/edit-category/${row?._id}`)}
                    >
                        <i className="bi bi-pencil-fill me-1"></i>Edit
                    </button>
                    <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => {
                            setItemToDelete(row);
                            setIsModalOpen(true);
                        }}
                    >
                        <i className="bi bi-trash-fill me-1"></i>Delete
                    </button>
                </div>
            ),
        },
    ];

    return (
        <React.Fragment>
            <Head title="Category List"></Head>
            <Content>
                <BlockHead size="sm">
                    <BlockBetween>
                        <BlockHeadContent>
                            <BlockTitle tag="h3" page>
                                Category List
                            </BlockTitle>
                            <BlockDes className="text-soft">
                                <p>You have a total of {categories.length} categories.</p>
                            </BlockDes>
                        </BlockHeadContent>
                        <BlockHeadContent>
                            <Button color="success" onClick={() => navigate("/add-category")}>
                                Add Category
                            </Button>
                        </BlockHeadContent>
                    </BlockBetween>
                </BlockHead>
                <BlockContent>
                    <PreviewCard>
                        {loading ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
                                <div className="text-center">
                                    <div
                                        className="spinner-grow text-primary"
                                        style={{ width: "3rem", height: "3rem" }}
                                        role="status"
                                    >
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-3 text-muted">Loading categories, please wait...</p>
                                </div>
                            </div>
                        ) : (
                            <ReactDataTable
                                data={categories}
                                columns={columns}
                                pagination
                                highlightOnHover
                            />
                        )}
                    </PreviewCard>
                </BlockContent>
            </Content>
            <DeleteModal
                isOpen={isModalOpen}
                toggle={() => setIsModalOpen(!isModalOpen)}
                onConfirm={handleDelete}
                item={itemToDelete}
            />
        </React.Fragment>
    );
};

export default CategoryList;
